import { useState } from 'react'
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { Alert, Button, Dialog, Icon, Link, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  FinancialAccount,
  FinancialType,
} from '@/reducers/finances/financialAccountsReducer'
import {
  deactivateFinancialAccount,
  DEACTIVATE_FINANCIAL_ACCOUNT_KEY,
} from '@/actions/financialAccountActions'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import {
  selectFinancialAccountById,
  selectFinancialAccounts,
} from '@/selectors/financeSelectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

type AccountNameProps = Pick<
  FinancialAccount,
  'plaidInstitutionName' | 'subtype' | 'mask'
>

const AccountName = ({
  plaidInstitutionName,
  subtype,
  mask,
}: AccountNameProps) => (
  <strong>
    {plaidInstitutionName} {subtype} ••••{mask}
  </strong>
)

const DefaultBody = ({ account }: { account: FinancialAccount }) => (
  <>
    <Text variant={TypographyStyles.BODY_SM}>
      <AccountName {...account} /> will stop syncing transactions. Your existing
      transaction history will be preserved.
    </Text>
    <Text variant={TypographyStyles.BODY_SM}>
      You can reactivate this account at any time.
    </Text>
  </>
)

const ManualBody = ({ account }: { account: FinancialAccount }) => (
  <>
    <Text variant={TypographyStyles.BODY_SM}>
      We will remove <AccountName {...account} /> from your list of active
      accounts.
    </Text>
    <Text variant={TypographyStyles.BODY_SM}>
      You can reactivate this account at any time.
    </Text>
  </>
)

const UnreconciledBody = ({
  account,
  unreconciledTransactionCount,
}: {
  account: FinancialAccount
  unreconciledTransactionCount: number
}) => (
  <>
    <Text variant={TypographyStyles.BODY_SM}>
      <AccountName {...account} /> has{' '}
      <strong>
        {unreconciledTransactionCount} unreconciled transaction
        {unreconciledTransactionCount === 1 ? '' : 's'}
      </strong>
      . Deactivating now may affect your bookkeeping records.
    </Text>
    <div className="flex items-start gap-3 rounded-lg bg-yellow-0 p-3">
      <Icon
        icon={faTriangleExclamation}
        size="sm"
        color="text-yellow-200"
        className="mt-0.5 shrink-0"
      />
      <Text variant={TypographyStyles.BODY_SM} className="text-yellow-200">
        We recommend reconciling these before deactivating your account.{' '}
        <Link href="/transactions" newPage className="font-medium">
          View transactions →
        </Link>
      </Text>
    </div>
  </>
)

const LastAccountBody = ({ account }: { account: FinancialAccount }) => (
  <>
    <Text variant={TypographyStyles.BODY_SM}>
      <AccountName {...account} /> is your only active business account.
    </Text>
    <Text variant={TypographyStyles.BODY_SM}>
      Make sure to connect another account to continue your bookkeeping.
    </Text>
  </>
)

/**
 * Off because the warning asks for something the member cannot do: reconciling
 * happens through the bookkeeper and the system, and the link it offers leads
 * somewhere nothing can be resolved. `UnreconciledBody` and everything gated on
 * this flag are kept so the warning can be switched back on once we settle what
 * signal it should fire on and what it should say.
 */
const SHOW_UNRECONCILED_WARNING = false

const DeactivateBodyRouter = ({
  account,
  isLastBusinessAccount,
  showUnreconciled,
}: {
  account: FinancialAccount
  isLastBusinessAccount: boolean
  showUnreconciled: boolean
}) => {
  switch (true) {
    case account.accountType === FinancialType.MANUAL:
      return <ManualBody account={account} />
    case isLastBusinessAccount:
      return <LastAccountBody account={account} />
    case showUnreconciled:
      return (
        <UnreconciledBody
          account={account}
          unreconciledTransactionCount={
            account.unreconciledTransactionCount ?? 0
          }
        />
      )
    default:
      return <DefaultBody account={account} />
  }
}

export const DeactivateAccountModal = ({
  accountId,
  onClose,
}: {
  accountId: number
  onClose: () => void
}) => {
  const dispatch = useAppDispatch()
  const account = useReselector(selectFinancialAccountById, accountId)
  const accounts = useReselector(selectFinancialAccounts)
  const isLastBusinessAccount =
    Object.values(accounts ?? {}).filter(
      (a) => !a.inactive && a.markedPersonalAt === null
    ).length === 1
  const isLoading = useReselector(
    getIsFetching,
    DEACTIVATE_FINANCIAL_ACCOUNT_KEY
  )
  const [attempted, setAttempted] = useState(false)
  const error = useReselector(getFetchError, DEACTIVATE_FINANCIAL_ACCOUNT_KEY)

  if (!account) return null

  const isManual = account.accountType === FinancialType.MANUAL
  const hasUnreconciled =
    SHOW_UNRECONCILED_WARNING && (account.unreconciledTransactionCount ?? 0) > 0
  const isRiskyDeactivation =
    !isManual && (isLastBusinessAccount || hasUnreconciled)
  const deactivateLabel =
    !isManual && hasUnreconciled && !isLastBusinessAccount
      ? 'Deactivate Anyway'
      : 'Deactivate'

  const handleDeactivate = async () => {
    setAttempted(true)
    const success = await dispatch(deactivateFinancialAccount(account.id))
    if (success) {
      onClose()
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content onDismiss={onClose}>
        <Text variant={TypographyStyles.H1}>Deactivate account?</Text>
        <div className="flex flex-col gap-2">
          <DeactivateBodyRouter
            account={account}
            isLastBusinessAccount={isLastBusinessAccount}
            showUnreconciled={hasUnreconciled}
          />
          {attempted && error && (
            <Alert variant="error" title="Couldn't deactivate account">
              {error.message}
            </Alert>
          )}
        </div>
        <Dialog.Footer className="sm:space-x-0">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant={isRiskyDeactivation ? 'danger' : undefined}
            loading={isLoading}
            onClick={handleDeactivate}
          >
            {deactivateLabel}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
